"use client";

import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { ILayoutsProfileSection, ProfileLayoutSectionEnum } from "@finbackoffice/fe-core";
import {
    useLayoutProfileConfig,
    useSearchParamsQuery,
    formatSearchParamsToString,
} from "@finbackoffice/site-core";
import { IModalForwardRefPropsV2 } from "contexts";
import { AssetsDirectory, TranslationScopes } from "@finbackoffice/enums";
import CasinoBetsHistory from "components/account/bets/casino/CasinoBetsHistory";
import SportBetsHistory from "components/account/bets/sport/SportBetsHistory";
import Modal from "components/base/modal/Modal";
import { ModalStyleTypeEnum } from "components/base/modal/constants/modal-config";
import { Svg } from "components/base/svg/Svg";
import { ModalTypes, RouterQuery } from "utils/constants";
import Button from "components/base/button/Button";
import Translate from "components/base/translate/Translate";
import classNames from "classnames";
import AccountBetsTabs from "components/account/bets/common/tabs/AccountBetsTabs";
import AccountHeader from "components/account/header/AccountHeader";
import styles from "./sections.module.sass";

const BetsSection: FC = () => {
    const router = useRouter();
    const pathname = usePathname();
    const { query } = useSearchParamsQuery();
    const { scope: scopeParam } = query;

    const betsHistoryModalRef = useRef<IModalForwardRefPropsV2>(null);
    const { config: betsSectionLayoutConfig } = useLayoutProfileConfig(
        ProfileLayoutSectionEnum.Bets,
    );
    const [scope, setScope] = useState<RouterQuery>((scopeParam as RouterQuery) || "");

    useEffect(() => {
        if (
            [RouterQuery.Sport, RouterQuery.LiveCasio, RouterQuery.Slots].includes(
                scope as RouterQuery,
            )
        ) {
            betsHistoryModalRef.current?.open();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateScope = useCallback((val: RouterQuery) => {
        setScope(val);
        betsHistoryModalRef.current?.open();
    }, []);

    const renderItem = useCallback(
        (item: ILayoutsProfileSection) => {
            let content;
            switch (item.name) {
                case ProfileLayoutSectionEnum.Sport:
                    content = (
                        <Button
                            type="button"
                            onClick={() => updateScope(RouterQuery.Sport)}
                            variant="custom"
                            data-testid="account-menu-item-sport-bets">
                            <Svg
                                src={`/${AssetsDirectory.Mobile}/${AssetsDirectory.ProfileCompact}/profile-sport.svg`}
                                className="svg-icon"
                                prefixAppName
                            />
                            <p>
                                <Translate
                                    tid="account_menu_bets_sport_heading"
                                    namespace={TranslationScopes.UserAccount}
                                />
                            </p>
                        </Button>
                    );
                    break;
                case ProfileLayoutSectionEnum.Livecasino:
                    content = (
                        <Button
                            type="button"
                            onClick={() => updateScope(RouterQuery.LiveCasio)}
                            variant="custom"
                            data-testid="account-menu-item-livecasino-bets">
                            <Svg
                                src={`/${AssetsDirectory.Mobile}/${AssetsDirectory.ProfileCompact}/profile-live-casino.svg`}
                                className="svg-icon"
                                prefixAppName
                            />
                            <p>
                                {" "}
                                <Translate
                                    tid="account_menu_bets_livecasino_heading"
                                    namespace={TranslationScopes.UserAccount}
                                />
                            </p>
                        </Button>
                    );
                    break;
                case ProfileLayoutSectionEnum.Slots:
                    content = (
                        <Button
                            type="button"
                            onClick={() => updateScope(RouterQuery.Slots)}
                            variant="custom"
                            data-testid="account-menu-item-slot-bets">
                            <Svg
                                src={`/${AssetsDirectory.Mobile}/${AssetsDirectory.ProfileCompact}/profile-slots.svg`}
                                className="svg-icon"
                                prefixAppName
                            />
                            <p>
                                <Translate
                                    tid="account_menu_bets_slots_heading"
                                    namespace={TranslationScopes.UserAccount}
                                />
                            </p>
                        </Button>
                    );
                    break;
                default:
                    return null;
            }

            return item.enable ? <li key={item.name}>{content}</li> : null;
        },
        [updateScope],
    );

    const onBetsHistoryModalClose = useCallback(() => {
        if (scopeParam) {
            const q = { ...query };
            delete q.scope;
            router.push(`${pathname}?${formatSearchParamsToString(q)}`);
        }
    }, [pathname, query, router, scopeParam]);

    const renderContent = useMemo(() => {
        switch (scope) {
            case RouterQuery.Sport:
                return <SportBetsHistory />;
            case RouterQuery.LiveCasio:
            case RouterQuery.Slots:
                return <CasinoBetsHistory />;
            default:
                return null;
        }
    }, [scope]);

    const renderTab = useCallback(
        (section: ILayoutsProfileSection) => {
            if (!section.enable) {
                return null;
            }

            switch (section.name) {
                case ProfileLayoutSectionEnum.Sport:
                    return (
                        <Button
                            key={section.name}
                            type="button"
                            variant="custom"
                            onClick={() => updateScope(RouterQuery.Sport)}
                            className={classNames({
                                ["myBetsTabActive"]: scope === RouterQuery.Sport,
                            })}>
                            <Svg
                                src={`/${AssetsDirectory.Mobile}/menu-sport.svg`}
                                className="svg-icon"
                                prefixAppName
                            />
                        </Button>
                    );
                case ProfileLayoutSectionEnum.Livecasino:
                    return (
                        <Button
                            key={section.name}
                            type="button"
                            variant="custom"
                            onClick={() => updateScope(RouterQuery.LiveCasio)}
                            className={classNames({
                                ["myBetsTabActive"]: scope === RouterQuery.LiveCasio,
                            })}>
                            <Svg
                                src={`/${AssetsDirectory.Common}/${AssetsDirectory.BaseIcons}/live-casino-icon.svg`}
                                className="svg-icon"
                                prefixAppName
                            />
                        </Button>
                    );
                case ProfileLayoutSectionEnum.Slots:
                    return (
                        <Button
                            key={section.name}
                            type="button"
                            variant="custom"
                            onClick={() => updateScope(RouterQuery.Slots)}
                            className={classNames({
                                ["myBetsTabActive"]: scope === RouterQuery.Slots,
                            })}>
                            <Svg
                                src={`/${AssetsDirectory.Common}/${AssetsDirectory.BaseIcons}/slots-icon.svg`}
                                className="svg-icon"
                                prefixAppName
                            />
                        </Button>
                    );
                default:
                    return null;
            }
        },
        [scope, updateScope],
    );

    return (
        <>
            <nav className={styles.subMenu} data-testid="account-bets-tab-container">
                {betsSectionLayoutConfig?.config?.map(renderItem)}
            </nav>
            <Modal
                priority
                ref={betsHistoryModalRef}
                type={ModalTypes.ACCOUNT_BETS_HISTORY}
                styleConfigType={ModalStyleTypeEnum.ProfileDetails}
                styleClass={styles.profileDetailsModal}
                animateVariant="bottom"
                closeButton
                onClose={onBetsHistoryModalClose}>
                <>
                    <div className={styles.myBets} data-testid="account-bets-content">
                        <AccountHeader
                            tId="account_bets_betsHistory"
                            headingIcon={`/${AssetsDirectory.Mobile}/${AssetsDirectory.ProfileCompact}/profile-my-bets.svg`}>
                            <AccountBetsTabs>
                                {betsSectionLayoutConfig?.config?.map(renderTab)}
                            </AccountBetsTabs>
                        </AccountHeader>
                        {renderContent}
                    </div>
                    <Button
                        name="close"
                        type="button"
                        className={styles.closeButton}
                        onClick={() => {
                            onBetsHistoryModalClose();
                            void betsHistoryModalRef.current?.close();
                        }}>
                        <Translate tid="base_close" />
                    </Button>
                </>
            </Modal>
        </>
    );
};

export default BetsSection;
